import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { lookerStudioMessageVisibilitySelector } from 'selectors/uiSelectors';
import MessageHolder from 'components/messages/MessageHolder';
import CopyService from 'mangools-commons/dist/services/CopyService';
import { userLookerStudioTokenSelector, fetchingUserLookerStudioTokenSelector } from 'selectors/userSelectors';
import { requestedUserLookerStudioTokenAction, requestedUserLookerStudioTokenUpdateAction } from 'actions/userActions';
import { gtmTrack } from 'actions/analyticsActions';
import { closeLookerStudioMessage } from 'actions/uiActions';
import { analyticsActions } from 'constants/analytics';
import { toast } from 'react-toastify';
import isNil from 'ramda/src/isNil';

function LookerStudioMessage() {
    const dispatch = useDispatch();
    const userLookerStudioToken = useSelector(userLookerStudioTokenSelector);
    const fetchingToken = useSelector(fetchingUserLookerStudioTokenSelector);

    const isVisible = useSelector(state => lookerStudioMessageVisibilitySelector(state));

    // Lazy loading: fetch token when modal becomes visible
    useEffect(() => {
        if (isVisible && isNil(userLookerStudioToken) && !fetchingToken) {
            dispatch(requestedUserLookerStudioTokenAction());
        }
    }, [isVisible]);

    const handleCopyClick = () => {
        CopyService.copy(userLookerStudioToken || '', { error: 'Sorry, you have to copy token manually!' })
            .then(success => {
                toast.info(success);
            })
            .catch(error => {
                toast.error(error.message);
            });
        dispatch(
            gtmTrack({
                action: analyticsActions.CLICK,
                event: 'looker_studio_token_copied',
            }),
        );
    };

    const requestUserLookerStudioTokenUpdate = () => {
        dispatch(requestedUserLookerStudioTokenUpdateAction());
    };

    if (isVisible) {
        return (
            <MessageHolder
                onClose={() => {
                    dispatch(closeLookerStudioMessage());
                }}
                classNames="uk-modal-dialog is-pricing uk-padding-remove"
            >
                <div className="mg-modal-header is-info">
                    <svg
                        width="100px"
                        height="100px"
                        viewBox="-78.5 0 413 413"
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                        xmlnsXlink="http://www.w3.org/1999/xlink"
                        preserveAspectRatio="xMidYMid"
                        style={{
                            fill: 'var(--blue)',
                        }}
                    >
                        <g>
                            <path d="M127.128486,0 C113.797782,0.0058471726 101.556004,7.36006381 95.2905253,19.126605 C89.0250469,30.8931461 89.7564532,45.1553578 97.1927396,56.2192339 L112.606279,40.8274339 C112.096845,39.2920176 111.839876,37.6841242 111.845385,36.066411 C111.845385,27.6618072 118.658663,20.8485297 127.063267,20.8485297 C135.467871,20.8485297 142.281148,27.6618072 142.281148,36.066411 C142.281148,44.4710148 135.467871,51.2842924 127.063267,51.2842924 C125.452814,51.2878362 123.852389,51.0308872 122.323984,50.5233983 L106.932184,65.9151983 C119.749817,74.6084738 136.686605,74.1479499 149.012895,64.7709924 C161.339185,55.3940349 166.302744,39.1943452 161.345227,24.5216568 C156.387711,9.84896827 142.61604,-0.0205786569 127.128486,0 L127.128486,0 Z" />
                            <path d="M112.780303,105.112113 C112.803794,92.9288201 108.858278,81.0693768 101.540706,71.3284161 L81.5400617,91.3073204 C87.7949796,102.747737 85.5440645,116.967804 76.0616244,125.917131 L86.9315396,152.483203 C103.037113,142.110661 112.772753,124.268811 112.780303,105.112113 Z" />
                            <path d="M56.8870939,133.786949 L56.3653379,133.786949 C44.0975407,133.788013 33.1858466,125.990585 29.2128405,114.383946 C25.2398344,102.777307 29.0843199,89.9287712 38.7794013,82.4118404 C48.4744826,74.8949096 61.8756692,74.3722813 72.126715,81.1113398 L91.9317006,61.3063543 C72.6737207,45.6936654 45.4778243,44.4893124 24.9151409,58.3385684 C4.35245741,72.1878245 -4.75374244,97.8421492 2.47549859,121.556363 C9.70473962,145.270576 31.5737,161.482171 56.3653379,161.50524 C60.1906548,161.507115 64.0066702,161.128427 67.7570091,160.374762 L56.8870939,133.786949 Z" />
                            <path d="M127.88938,156.76595 C115.371706,156.753269 102.919887,158.577095 90.9316684,162.179168 L106.780005,200.897806 C113.678715,199.188192 120.760254,198.326726 127.86764,198.332506 C169.050784,198.344513 204.491034,227.444917 212.516351,267.838552 C220.541668,308.232187 198.917134,348.670095 160.866351,364.424121 C122.815568,380.178148 78.9350487,366.861058 56.0581359,332.616375 C33.1812232,298.371692 37.6787581,252.735993 66.8004566,223.615929 C72.8771111,217.558264 79.8143655,212.430409 87.3880761,208.398047 L71.7136583,169.788108 C13.2865745,198.402523 -14.3767247,266.297107 7.41546106,327.59645 C29.2076468,388.895793 93.5203541,424.092503 156.898395,409.404661 C220.276436,394.716818 262.550912,334.818559 255.157557,270.182291 C247.764201,205.546024 193.055814,156.741054 127.998079,156.74421 L127.88938,156.76595 Z" />
                        </g>
                    </svg>
                </div>
                <div className="mg-modal-content">
                    <h3 className="font-24 uk-text-bold">Copy your authentication token</h3>
                    <div className="uk-width-medium-8-10 uk-container-center uk-clearfix">
                        <p className="mg-alert is-info mg-margin-b-10">
                            <FontAwesomeIcon icon="info-circle" className="mg-margin-r-10" aria-hidden="true" />
                            <strong>
                                To add SERPWatcher data to your Looker Studio reports, you will need to authorize
                                Mangools SW connector with the token below
                            </strong>
                        </p>
                        <div className="mg-margin-b-10 uk-flex uk-hidden-small">
                            <span className="uk-text-left font-14">Never used a Looker Studio connector?</span>
                            <a
                                href="https://mangools.com/blog/google-looker-studio-connector/?ref=sw-gls-modal"
                                target="_blank"
                                className="mg-btn is-link is-xsmall uk-text-bold uk-float-right mg-margin-l-10"
                                title="Mangools SERPWatcher connector"
                                type="button"
                                disabled={fetchingToken}
                                onClick={() => {
                                    dispatch(
                                        gtmTrack({
                                            action: analyticsActions.CLICK,
                                            event: 'looker_studio_connector_guide',
                                        }),
                                    );
                                }}
                                rel="noreferrer"
                            >
                                <FontAwesomeIcon aria-label="Mangools SERPWatcher connector" icon="external-link-alt" />
                                Learn how it works
                            </a>
                        </div>
                        <div className="mg-margin-b-10 uk-text-center uk-visible-small">
                            <span className="font-14">Never used a Looker Studio connector?</span>
                            <a
                                href="https://mangools.com/blog/google-looker-studio-connector/?ref=sw-gls-modal"
                                target="_blank"
                                className="mg-btn is-link is-xsmall uk-text-bold mg-margin-t-5"
                                title="Mangools SERPWatcher connector"
                                type="button"
                                disabled={fetchingToken}
                                onClick={() => {
                                    dispatch(
                                        gtmTrack({
                                            action: analyticsActions.CLICK,
                                            event: 'looker_studio_connector_guide',
                                        }),
                                    );
                                }}
                                rel="noreferrer"
                                style={{ display: 'block' }}
                            >
                                <FontAwesomeIcon aria-label="Mangools SERPWatcher connector" icon="external-link-alt" />
                                Learn how it works
                            </a>
                        </div>
                        <div className="uk-flex uk-hidden-small" style={{ width: '100%' }}>
                            <div className="mg-input-container is-icon is-button uk-flex-item-auto">
                                <input
                                    className="mg-input font-14 mg-truncate report-share-url"
                                    disabled={fetchingToken}
                                    readOnly
                                    style={{ pointerEvents: fetchingToken ? 'none' : 'auto' }}
                                    type="text"
                                    value={userLookerStudioToken || ''}
                                />
                                <span className="mg-input-icon">
                                    <FontAwesomeIcon icon="copy" aria-hidden="true" />
                                </span>
                                <button
                                    className="mg-btn is-green uk-text-bold"
                                    title="Click to copy"
                                    type="button"
                                    onClick={handleCopyClick}
                                    disabled={fetchingToken}
                                >
                                    Copy
                                </button>
                            </div>
                        </div>
                        <div className="uk-visible-small">
                            <button
                                className="mg-btn is-green uk-text-bold uk-width-1-1"
                                title="Click to copy"
                                type="button"
                                onClick={handleCopyClick}
                            >
                                Copy to clipboard
                            </button>
                        </div>
                        <div className="uk-flex uk-flex-column-small uk-flex-space-between uk-text-center-small">
                            <button
                                className="mg-btn is-link is-small uk-text-bold mg-margin-t-15"
                                disabled={fetchingToken}
                                onClick={requestUserLookerStudioTokenUpdate}
                                title="Generate new token"
                                type="button"
                            >
                                <FontAwesomeIcon
                                    aria-label="Generate new token"
                                    title="Generate new token"
                                    icon="sync"
                                />
                                {!fetchingToken ? 'Generate new token' : 'Generating...'}
                            </button>
                            <a
                                onClick={() => {
                                    dispatch(
                                        gtmTrack({
                                            action: analyticsActions.CLICK,
                                            event: 'looker_studio_connector_link',
                                        }),
                                    );
                                }}
                                target="_blank"
                                href="https://datastudio.google.com/datasources/create?connectorId=AKfycbxsl5BqzhFIlte0GoX9dyAeNl70MHbRjGweuKCMxKHp_zHr_VdtSlY5tPgb7BkeK4W5"
                                className="mg-btn is-link is-small uk-text-bold mg-margin-t-15"
                                title="Mangools SERPWatcher connector"
                                type="button"
                                disabled={fetchingToken}
                                rel="noreferrer"
                            >
                                <FontAwesomeIcon aria-label="Mangools SERPWatcher connector" icon="external-link-alt" />
                                Mangools SERPWatcher connector
                            </a>
                        </div>
                    </div>
                </div>
            </MessageHolder>
        );
    } else {
        return null;
    }
}

export default LookerStudioMessage;
